/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, useRef } from 'preact/hooks';
import { ComponentChildren } from 'preact';
import { Collapsible } from '../UNSAFE_Collapsible';
//rename accordion item context
import { AccordionItemStylingContext } from '../PRIVATE_AccordionItemStylingContext';

import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';

type TransitionEnd<K> = {
  value:  {
    from?: Array<K>;
    to: Array<K> | null
  }
};


/**
 * Props for the  AccordionItemMultiple Component
 */
export type Props<K extends string | number> = {
  /**
   * The key associated with the  AccordionItemMultiple.
   */
  itemKey: K;
  /**
   * The expanded key.
   */
  expandedKeys?: Array<K>;
  /**
   * aria-label for this  AccordionItemMultiple
   */
  'aria-label'?: string;
  /**
   * Callback function to invoke when the expanded keys has changed when
   * the item is toggled.
   */
  onChange?: (detail: { value: Array<K>; target: EventTarget | null;}) => void;
  /**
   * One or more ids (separated by spaces) of elements that label the selected item. This will be used for the aria-labelledby of the DOM element.
   */
  'aria-labelledby'?: string;

  /**
   * AccordionItem's header. If not specified, the header contains only an open/close icon. Note that the header text is required for JET collapsible for accessibility purposes.
   */
  header?: ComponentChildren;

  /**
   * The content node to be collapsed
   */
  children?: ComponentChildren;

  /**
   * Disables the collapsible if set to true
   */
  isDisabled?: boolean;

  /**
   * Changes chevron icon placement at the end of the collapsible header
   */
  iconPosition?: 'start' | 'end';


   /**
   * Property that triggers a callback after toggle animation is done
   */
  onTransitionEnd?: (details: TransitionEnd<K>) => void;
};

export type ToggleDetail =  {
  value: boolean;
  target: EventTarget | null;
};


/**
 * The  AccordionItemMultiple component renders checkboxes in collections to support selection.
 */
export function  AccordionItemMultiple<K extends string | number>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  children,
  isDisabled,
  header,
  iconPosition,
  itemKey,
  expandedKeys,
  onChange,
  onTransitionEnd,
  testId
}: Props<K> & TestIdProps) {

  // Track previous expandedKey during render phase
  const seenExpandedKeysRef = useRef<Array<K> | undefined>(expandedKeys);
  const prevExpandedKeysRef = useRef<Array<K>>();

  // capture during render before DOM events fire
  if (!keysEqual(seenExpandedKeysRef.current, expandedKeys)) {
    prevExpandedKeysRef.current = seenExpandedKeysRef.current;
    seenExpandedKeysRef.current = expandedKeys;
  }
  const onToggleHandler = useCallback(
    (detail: ToggleDetail) => {
      handleToggle(detail, itemKey, expandedKeys, onChange);
    },
    [itemKey, expandedKeys, onChange]
  );

  const toggleHandler = useCallback(
    (detail: ToggleDetail) => {
      onToggleHandler(detail);
    },
    [onToggleHandler]
  );

  const handleOnTransitionEnd = useCallback(
    () => {
      onTransitionEnd?.(
        {
        value: {from:prevExpandedKeysRef.current, to: expandedKeys || [] },
      });
    },
    [onTransitionEnd, expandedKeys]
  )

  const isExpanded =  expandedKeys?.includes(itemKey);

  const testIdProps = useTestId(testId);

  return (
    <div
      {...testIdProps}>
    <AccordionItemStylingContext.Provider value={{stylingVariant: 'accordion'}}> 
      <Collapsible
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        isDisabled={isDisabled}
        isExpanded={isExpanded}
        header={header}
        iconPosition={iconPosition}
        variant='basic'
        onToggle={toggleHandler}
        onTransitionEnd={handleOnTransitionEnd}>
        {children}
      </Collapsible>
    </AccordionItemStylingContext.Provider> 

    </div>
  );
}

const keysEqual = <K extends string | number>(a: Array<K> | undefined, b: Array<K> | undefined): boolean => {
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const aSorted = [...a].sort();
  const bSorted = [...b].sort();
  return aSorted.every((v, i) => v === bSorted[i]);
};

const handleToggle = <K extends string | number>(
  detail: ToggleDetail,
  itemKey: Props<K>['itemKey'],
  expandedKeys: Props<K>['expandedKeys'],
  onChange?: Props<K>['onChange']
) => {  
  if (onChange) {
    const newExpandedKeys = getNewExpandedKeys(itemKey, expandedKeys);
    onChange({ value: newExpandedKeys ?? [], target: detail.target });
   }
};

const getNewExpandedKeys = <K extends string | number>(
  itemKey: K,
  expandedKeys: Array<K> | undefined
): Array<K> => {
   if (expandedKeys?.includes(itemKey)) {
    // Remove the item — return a new array without it
    return expandedKeys.filter(key => key !== itemKey);
  } else {
    // Add the item — return a new array with it
    return [...(expandedKeys ?? []), itemKey];
  }
}
